import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import AngleViewDropdown from '../../components/AngleViewDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import { setGeneratedImageDragData } from '../../utils/dragDropUtils';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { buildFreestyleInstruction, generateFreestyle, getErrorMessage } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useAuth } from '../../contexts/AuthContext';
import { userProfilesService, projectsService, savedDesignsService, generationMetadataService, storageService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';
import { downloadImage } from '../../utils/downloadUtils';

const FreestyleField = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { userProfile, user: authUser } = useAuth();
  const [remainingCredits] = useState(100);
  const [monthlyLimit] = useState(500);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Prompt
  const [promptText, setPromptText] = useState('');
  const textareaRef = useRef(null);

  // Uploaded images
  const [uploadedImages, setUploadedImages] = useState([]);

  // Model
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');

  // Aspect Ratio
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);

  // Virtual Tool Guide
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const totalGuideSteps = 9;
  // Step 1: upload+prompt, Step 2: model, Step 3: aspect ratio, Step 4: resolution,
  // Step 5: batch count, Step 6: angle view, Step 7: generate btn, Step 8: asset library, Step 9: result window
  const uploadPromptGuideRef = useRef(null);
  const modelGuideRef = useRef(null);
  const batchGuideRef = useRef(null);
  const angleGuideRef = useRef(null);
  const generateBtnGuideRef = useRef(null);
  const assetLibraryGuideRef = useRef(null);
  const resultGuideRef = useRef(null);

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    setGuideVisible(false);
    setTimeout(() => {
      uploadPromptGuideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
    }, 500);
  };

  const nextGuideStep = () => {
    if (guideAnimating) return;
    if (guideStep >= totalGuideSteps) { closeGuide(); return; }
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const allRefs = [uploadPromptGuideRef, modelGuideRef, aspectRatioRef, resolutionRef, batchGuideRef, angleGuideRef, generateBtnGuideRef, assetLibraryGuideRef, resultGuideRef];
    const needsScroll = [5, 6, 7, 8, 9].includes(nextStep);
    if (needsScroll) {
      const ref = allRefs[nextStep - 1]?.current;
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
        }, 400);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
      setGuideAnimating(false);
    }, 600);
  };

  // Angle view
  const [selectedAngleView, setSelectedAngleView] = useState('--');

  // Batch count
  const [batchCount, setBatchCount] = useState(1);
  const maxBatch = 4;

  // Generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedResultIdx, setSelectedResultIdx] = useState(0);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);

  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  // Load project from project-history navigation
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProject = async () => {
      try {
        setCurrentProjectId(projectId);
        const designs = await savedDesignsService.getDesigns(projectId);
        if (designs && designs.length > 0) {
          // Restore generated images (each design = one image via image_url)
          const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
          if (imageUrls.length > 0) setGeneratedImages(imageUrls);

          // Restore prompt from the most recent design
          const latestDesign = designs[0]; // getDesigns returns DESC by created_at
          setCurrentDesignId(latestDesign.id);
          if (latestDesign?.prompt) setPromptText(latestDesign.prompt);

          // Restore generation settings from metadata of the latest design
          try {
            const metadata = await generationMetadataService.getMetadata(latestDesign.id);
            if (metadata) {
              if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
              if (metadata.imageSize) setSelectedAspectRatio(metadata.imageSize);
              if (metadata.resolution) setSelectedResolution(metadata.resolution);
              if (metadata.angleView) setSelectedAngleView(metadata.angleView);
              if (metadata.additionalParams?.batchCount) setBatchCount(metadata.additionalParams.batchCount);
              if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
            }
          } catch (metaErr) {
            console.error('Error loading freestyle metadata:', metaErr);
          }
        }
      } catch (err) {
        console.error('Error loading freestyle project:', err);
      }
    };
    loadProject();
  }, [location.state?.projectId]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const remaining = Math.max(0, 6 - uploadedImages.length);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const urls = (await Promise.all(limitedFiles.map((f) => storageService.uploadFile(f, authUser?.id)))).filter(Boolean);
    setUploadedImages((prev) => [...prev, ...urls]);
  };

  const handleRemoveImage = (idx) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    if (!promptText.trim()) { isGeneratingRef.current = false; return; }

    // Auth check
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }

    // Credit check (supports team members sharing owner's pool)
    const totalCost = batchCount * getModelCreditCost(selectedModel);
    const creditCheck = await userProfilesService?.checkCredits(session?.user?.id, totalCost);
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(session?.user?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsGenerating(true);
      startTimer();
      setSelectedResultIdx(0);

      const dimensions = getResolutionDimensions(selectedResolution);
      const sharedPrompt = buildFreestyleInstruction({
        promptText: promptText.trim(),
        selectedAngleView,
        hasReferenceImages: uploadedImages.length > 0,
      });

      // Run batchCount generations in parallel
      const generationTasks = Array.from({ length: batchCount }, () => {
        if (isFluxModel(selectedModel)) {
          return generateWithFlux({
            modelName: selectedModel,
            directPromptText: sharedPrompt,
            userPromptText: promptText.trim(),
            referenceImages: uploadedImages,
            selectedAngleShot: selectedAngleView !== '--' ? selectedAngleView : '',
            imageSize: selectedAspectRatio,
            signal: controller.signal,
          });
        } else {
          return generateFreestyle({
            promptText: promptText.trim(),
            referenceImages: uploadedImages,
            modelName: selectedModel,
            selectedAngleView,
            dimensions,
            aspectRatio: selectedAspectRatio,
            signal: controller.signal,
          });
        }
      });

      const results = await Promise.allSettled(generationTasks);
      const successImages = results
        .filter((r) => r.status === 'fulfilled' && r.value)
        .map((r) => r.value);

      if (successImages.length === 0) {
        throw new Error('All generations failed. Please try again.');
      }

      setGeneratedImages((prev) => [...successImages, ...prev]);
      clearTimer();
      toast.success(`Generated ${successImages.length} image${successImages.length > 1 ? 's' : ''} successfully!`);

      // Save to Supabase project
      try {
        const activeUser = authUser || session?.user;
        if (activeUser?.id) {
          let projectId = currentProjectId;
          if (!projectId) {
            const { data: project } = await projectsService?.createProject(activeUser.id, {
              name: `Freestyle – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
              toolName: 'Freestyle Field',
              toolType: 'freestyle',
            });
            projectId = project?.id;
            setCurrentProjectId(projectId);
          }
          if (projectId) {
            await projectsService?.incrementVariations(projectId, successImages.length);
            await projectsService?.updateProject(projectId, { thumbnail: successImages[0] });

            // Save each generated image as a separate saved_design
            // (saved_designs requires image_url NOT NULL)
            for (const imageUrl of successImages) {
              const { data: design } = await savedDesignsService?.createDesign(activeUser.id, projectId, {
                imageUrl,
                prompt: promptText.trim(),
                isFavorite: false,
              });

              // Save generation settings to generation_metadata
              if (design?.id) {
                await generationMetadataService?.createMetadata(activeUser.id, design.id, {
                  modelUsed: selectedModel,
                  imageSize: selectedAspectRatio,
                  resolution: selectedResolution,
                  style: 'Freestyle',
                  angleView: selectedAngleView !== '--' ? selectedAngleView : '',
                  quality: 'High',
                  usedMedia: uploadedImages,
                  additionalParams: {
                    batchCount,
                  },
                });
              }
            }
          }
        }
      } catch (saveErr) {
        console.error('Error saving freestyle project:', saveErr);
      }

      // Deduct credits based on model cost
      await userProfilesService?.updateCredits(session.user.id, batchCount * getModelCreditCost(selectedModel));

    } catch (error) {
      if (error?.message?.includes('cancelled') || error?.name === 'AbortError') {
        setCancelled();
        toast('Generation cancelled.', { icon: '✋' });
      } else {
        setFailed();
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  const handleDeleteResult = (idx) => {
    setGeneratedImages((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      if (selectedResultIdx >= updated.length) {
        setSelectedResultIdx(Math.max(0, updated.length - 1));
      }
      return updated;
    });
  };

  const handleDeleteAll = () => {
    setGeneratedImages([]);
    setSelectedResultIdx(0);
    setIsGenerating(false);
  };

  const handleDownload = async (idx) => {
    const img = generatedImages[idx ?? selectedResultIdx];
    if (!img) return;
    await downloadImage(img, `freestyle-design-${(idx ?? selectedResultIdx) + 1}.png`);
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [promptText]);

  // Close aspect ratio dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (aspectRatioRef.current && !aspectRatioRef.current.contains(e.target)) {
        setIsAspectRatioOpen(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(e.target)) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentRatio = aspectRatios.find((r) => r.value === selectedAspectRatio);

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedAspectRatio] || resolutionMap['1:1'];
    return ratioMap ? ratioMap[resKey] || { width: 1024, height: 1024 } : { width: 1024, height: 1024 };
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>Freestyle Field - Roomwork.ai</title>
        <meta name="description" content="Create unique interior designs with complete creative freedom" />
      </Helmet>
      <div className="min-h-screen bg-black flex flex-col overflow-x-hidden">
        <Header showCredits={true} remainingCredits={remainingCredits} monthlyLimit={monthlyLimit} />

        <main className="flex-1 flex flex-col pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1400px] mx-auto w-full flex flex-col flex-1">

            {/* Header Section */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>

              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">Freestyle</h1>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => openGuide()}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
                    <Icon name="HelpCircle" size={18} />
                    <span className="hidden md:inline">Virtual Tool Guide</span>
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Result Window */}
            <div
              ref={resultGuideRef}
              className="glassmorphic-card rounded-3xl p-4 md:p-6 mb-6 max-w-[960px] mx-auto w-full"
              style={{ boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : undefined, transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined }}
            >
              <div className="flex justify-end items-center mb-4">
                <h3 className="text-[12.6px] font-medium text-white">Result</h3>
              </div>

              {generatedImages.length > 1 ? (
                /* Multi-image grid */
                <div className={`grid gap-3 ${generatedImages.length === 2 ? 'grid-cols-2' : generatedImages.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {generatedImages.map((img, idx) => (
                    <div
                      key={idx}
                      className={`relative rounded-2xl overflow-hidden bg-black/40 group cursor-pointer transition-all ${
                        selectedResultIdx === idx ? 'ring-2 ring-orange-500' : 'ring-1 ring-white/10 hover:ring-white/30'
                      }`}
                      onClick={() => setSelectedResultIdx(idx)}
                    >
                      <img
                        src={img}
                        alt={`Generated result ${idx + 1}`}
                        className="w-full h-auto object-contain cursor-grab active:cursor-grabbing"
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); setGeneratedImageDragData(e, img); }}
                      />
                      {/* Per-image action buttons */}
                      <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(idx); }}
                          className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Download">
                          <Icon name="Download" size={16} className="text-white" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteResult(idx); }}
                          className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Delete">
                          <Icon name="Trash2" size={16} className="text-white" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGenerate(); }}
                          className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all"
                          title="Regenerate">
                          <Icon name="RefreshCw" size={16} className="text-white" />
                        </button>
                      </div>
                      {/* Image index badge */}
                      <div className="absolute top-3 left-3 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center">
                        <span className="text-white text-xs font-medium">{idx + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Single image or empty state */
                <div className="relative rounded-2xl overflow-hidden bg-black/40 border-2 border-dashed border-white/20 h-[250px] md:h-[504px]">
                  {isGenerating ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                        <p className="text-white/70 text-sm">Generating your design...</p>
                        <p className="text-white/50 text-xs mt-2">This may take a few moments</p>
                      </div>
                    </div>
                  ) : generatedImages.length === 1 ? (
                    <img
                      src={generatedImages[0]}
                      alt="Generated result"
                      className="w-full h-full object-contain cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => setGeneratedImageDragData(e, generatedImages[0])} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                        <p className="text-white/50 text-sm">No image generated yet</p>
                        <p className="text-white/30 text-xs mt-1">Enter a prompt and click Generate</p>
                      </div>
                    </div>
                  )}

                  {/* Action buttons for single image */}
                  <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                    <button
                      onClick={() => handleDownload(0)}
                      disabled={generatedImages.length === 0 || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download">
                      <Icon name="Download" size={18} className="text-white" />
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      disabled={generatedImages.length === 0 || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete">
                      <Icon name="Trash2" size={18} className="text-white" />
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={generatedImages.length === 0 || isGenerating}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate">
                      <Icon name="RefreshCw" size={18} className="text-white" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Counter */}
            <div className="text-left mb-2 max-w-[960px] mx-auto w-full">
              <span className={`text-sm font-medium ${uploadedImages.length >= 6 ? "text-red-400" : "text-white/70"}`}>
                {uploadedImages.length} / 6 images uploaded
              </span>
            </div>

            {/* Asset Library */}
            <div
              ref={assetLibraryGuideRef}
              className="max-w-[960px] mx-auto w-full"
              style={{ borderRadius: '1.5rem', boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined }}
            >
              <AssetLibraryPanel
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                generatedImages={generatedImages}
                setGeneratedImages={setGeneratedImages}
                showDragHint={true}
                currentDesignId={currentDesignId}
                currentProjectId={currentProjectId}
                onImageDeleted={(remaining) => {
                  if (!isGenerating) {
                    setSelectedResultIdx(remaining.length > 0 ? 0 : 0);
                  }
                }}
              />
            </div>

            {/* Prompt Task Bar */}
            <div
              className="glassmorphic-card rounded-3xl p-6 border border-orange-500/20 shadow-[0_0_30px_rgba(249,115,22,0.15)] max-w-[960px] mx-auto w-full mb-8"
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
              onDrop={async (e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-orange-500/50');
                const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                if (imageUrl) {
                  if (uploadedImages.length < 6) {
                    setUploadedImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                  }
                  return;
                }
                const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                if (files.length > 0) {
                  const remaining = Math.max(0, 6 - uploadedImages.length);
                  const limited = files.slice(0, remaining);
                  const urls = (await Promise.all(limited.map(f => storageService.uploadFile(f, authUser?.id)))).filter(Boolean);
                  setUploadedImages(prev => [...prev, ...urls]);
                }
              }}
            >

              {/* First Level: Plus Button + Textarea */}
              <div
                ref={uploadPromptGuideRef}
                className="flex items-start gap-3 mb-6"
                style={{
                  borderRadius: '1rem',
                  boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent',
                  transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: showGuide && guideStep === 1 ? 'relative' : undefined,
                  zIndex: showGuide && guideStep === 1 ? 201 : undefined,
                }}
              >
                {/* Upload Image Icon */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <label className={`flex-shrink-0 ${uploadedImages.length >= 6 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadedImages.length >= 6}
                    />
                    <div className="w-12 h-12 glassmorphic-button rounded-2xl flex items-center justify-center hover:scale-105 transition-transform">
                      <Icon name="Plus" size={20} className="text-white" />
                    </div>
                  </label>
                </div>

                {/* Textarea */}
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); } }}
                    placeholder="Describe the scene you imagine..."
                    rows={5}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-orange-500/50 px-3 py-2 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px]"
                    style={{ height: '120px' }}
                  />

                  {/* Display uploaded images in textarea zone */}
                  {uploadedImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {uploadedImages.map((img, idx) => (
                        <UploadedImageThumb key={`upload-${idx}`} src={img} alt={`Upload ${idx + 1}`} onRemove={() => handleRemoveImage(idx)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Second Level: Model Selection + Aspect Ratio */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Model Selection */}
                  <div
                    ref={modelGuideRef}
                    style={{
                      borderRadius: '1rem',
                      boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent',
                      transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: showGuide && guideStep === 2 ? 'relative' : undefined,
                      zIndex: showGuide && guideStep === 2 ? 201 : undefined,
                    }}
                  >
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                    />
                  </div>

                  {/* Aspect Ratio */}
                  <div
                    ref={(el) => { aspectRatioRef.current = el; }}
                    style={{
                      borderRadius: '1rem',
                      boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent',
                      transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: showGuide && guideStep === 3 ? 'relative' : undefined,
                      zIndex: showGuide && guideStep === 3 ? 201 : undefined,
                    }}
                  >
                  <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                      className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <AspectRatioIcon
                          ratio={selectedAspectRatio}
                          size={18}
                          className="text-orange-500"
                        />
                        <span className="text-white text-sm">{selectedAspectRatio}</span>
                      </div>
                      <Icon
                        name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        className="text-white/50"
                      />
                    </button>

                    {/* Dropdown menu */}
                    {isAspectRatioOpen && (
                      <div
                        className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.95)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.value}
                            onClick={() => {
                              setSelectedAspectRatio(ratio.value);
                              setIsAspectRatioOpen(false);
                            }}
                            className={`w-full flex items-center px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                              selectedAspectRatio === ratio.value
                                ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                : 'hover:bg-white/5'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <AspectRatioIcon
                                ratio={ratio.value}
                                size={18}
                                className={selectedAspectRatio === ratio.value ? 'text-orange-500' : 'text-white/50'}
                              />
                              <span className={`text-sm ${selectedAspectRatio === ratio.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                {ratio.label}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  </div>
                </div>

                {/* Resolution Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    ref={(el) => { resolutionRef.current = el; }}
                    style={{
                      borderRadius: '1rem',
                      boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent',
                      transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: showGuide && guideStep === 4 ? 'relative' : undefined,
                      zIndex: showGuide && guideStep === 4 ? 201 : undefined,
                    }}
                  >
                  {!isFluxModel(selectedModel) && (
                  <>
                  <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                  <div className="relative">
                    <button
                      onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                      className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                    >
                      <span className="text-white text-sm font-medium">{selectedResolution}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white/50 text-xs">
                            {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                          </span>
                        <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                      </div>
                    </button>

                    {/* Dropdown menu */}
                    {isResolutionOpen && (
                      <div
                        className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.95)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        {RESOLUTION_OPTIONS.map((resKey) => {
                          const dims = getResolutionDimensions(resKey);
                          return (
                            <button
                              key={resKey}
                              onClick={() => {
                                setSelectedResolution(resKey);
                                setIsResolutionOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                selectedResolution === resKey
                                  ? 'bg-orange-500/20 hover:bg-orange-500/30'
                                  : 'hover:bg-white/5'
                              }`}
                            >
                              <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                {resKey}
                              </span>
                              <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                  {dims.width}×{dims.height}
                                </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  </>
                  )}
                  </div>
                </div>
              </div>

              {/* Third Level: Batch Count + Angle View + Generate */}
              <div className="space-y-4">
                {/* Batch Count + Angle View - same row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    ref={batchGuideRef}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined }}
                  >
                    <label className="text-white/70 text-xs mb-2 block">Generating batches</label>
                    <div className="flex items-center gap-1.5">
                      {[1, 2, 3, 4].map((num) => (
                        <button
                          key={num}
                          onClick={() => setBatchCount(num)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${
                            batchCount === num
                              ? 'glassmorphic-button text-white'
                              : 'bg-white/5 border border-white/10 text-white/50 hover:border-white/20 hover:text-white/70'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div
                    ref={angleGuideRef}
                    style={{ borderRadius: '1rem', boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined }}
                  >
                    <AngleViewDropdown value={selectedAngleView} onChange={(val) => setSelectedAngleView(val)} />
                  </div>
                </div>

                {/* Generate Button */}
                <div
                  ref={generateBtnGuideRef}
                  className="rounded-2xl"
                  style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                  <button
                    onClick={handleGenerate}
                    disabled={!promptText.trim() || isGenerating}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <Icon name="Loader" size={18} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Icon name="Sparkles" size={18} />
                        Generate Image
                      </>
                    )}
                  </button>
                </div>

                {isGenerating && (
                  <button
                    onClick={handleCancelGeneration}
                    className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Cancel
                  </button>
                )}

                <p className="text-white/50 text-xs text-center">
                  Your request will cost {batchCount * getModelCreditCost(selectedModel)} credits
                </p>
              </div>
            </div>

          </div>
        </main>
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={closeGuide}>
            <div
              className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
            />
          </div>

          {/* Tooltip bubble */}
          <div
            className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const refs = [uploadPromptGuideRef.current, modelGuideRef.current, aspectRatioRef.current, resolutionRef.current, batchGuideRef.current, angleGuideRef.current, generateBtnGuideRef.current, assetLibraryGuideRef.current, resultGuideRef.current];
              const ref = refs[guideStep - 1];
              const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              // Steps 1, 8, 9 (full-width elements): bubble below, centered, up-arrow
              if (guideStep === 1 || guideStep === 8 || guideStep === 9) {
                return {
                  top: rect.bottom + 16,
                  left: Math.max(12, Math.min(window.innerWidth - bubbleWidth - 12, rect.left + rect.width / 2 - bubbleWidth / 2)),
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0px)' : 'translateY(-8px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Step 7 (generate button, full-width): bubble above, centered, down-arrow
              if (guideStep === 7) {
                return {
                  top: rect.top - 230,
                  left: Math.max(12, Math.min(window.innerWidth - bubbleWidth - 12, rect.left + rect.width / 2 - bubbleWidth / 2)),
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0px)' : 'translateY(8px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Steps 3, 6 (right-column elements): bubble to the LEFT
              if (guideStep === 3 || guideStep === 6) {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left - bubbleWidth - 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Steps 2, 4, 5 (left-column elements): bubble to the RIGHT
              return {
                top: rect.top + rect.height / 2,
                left: rect.right + 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
              {/* Arrow direction:
                  Steps 1, 8, 9 (bubble below): up-pointing arrow at top-center
                  Step 7 (bubble above): down-pointing arrow at bottom-center
                  Steps 3, 6 (bubble to left): right-pointing arrow at right-center
                  Steps 2, 4, 5 (bubble to right): left-pointing arrow at left-center
              */}
              {(guideStep === 1 || guideStep === 8 || guideStep === 9) ? (
                <>
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderBottom: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : guideStep === 7 ? (
                <>
                  <div
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderTop: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : (guideStep === 3 || guideStep === 6) ? (
                <>
                  <div
                    className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: '9px solid rgba(30, 30, 30, 0.95)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : (
                <>
                  <div
                    className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderRight: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderRight: '9px solid rgba(30, 30, 30, 0.95)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              )}

              <div
                className="rounded-2xl p-5 text-center"
                style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 mb-2"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s' }}
                >
                  <Icon
                    name={
                      guideStep === 1 ? 'Upload' :
                      guideStep === 2 ? 'Cpu' :
                      guideStep === 3 ? 'RectangleHorizontal' :
                      guideStep === 4 ? 'SlidersHorizontal' :
                      guideStep === 5 ? 'Grid2x2' :
                      guideStep === 6 ? 'Eye' :
                      guideStep === 7 ? 'Sparkles' :
                      guideStep === 8 ? 'Library' :
                      'ImagePlay'
                    }
                    size={18}
                    className="text-white/50"
                  />
                  <span className="text-sm font-semibold text-white">Step {guideStep}</span>
                </div>
                <p
                  className="text-sm text-white/80 leading-relaxed"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s' }}
                >
                  {guideStep === 1 ? (
                    <>Upload up to <span className="text-white font-medium italic">6 reference images</span> to guide the AI — mix styles, rooms, or moods. Then <span className="text-white font-medium italic">describe your vision freely</span> in the prompt field. No templates, no restrictions.</>
                  ) : guideStep === 2 ? (
                    <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                  ) : guideStep === 3 ? (
                    <>Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different <span className="text-white font-medium italic">ratios</span> allow you to explore different compositions.</>
                  ) : guideStep === 4 ? (
                    <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                  ) : guideStep === 5 ? (
                    <>Set how many images to generate in a single run — up to <span className="text-white font-medium italic">4 variations</span> at once. Use batch mode to explore multiple creative directions simultaneously without re-prompting.</>
                  ) : guideStep === 6 ? (
                    <>Choose a specific <span className="text-white font-medium italic">camera angle</span> for your render — eye-level, bird's eye, worm's eye, and more. Leave it on <span className="text-white font-medium italic">--</span> to let the AI decide the best perspective for your scene.</>
                  ) : guideStep === 7 ? (
                    <>Hit <span className="text-white font-medium italic">Generate</span> to bring your vision to life. The AI will process your prompt, reference images, and all selected settings to produce your design.</>
                  ) : guideStep === 8 ? (
                    <>The <span className="text-white font-medium italic">Asset Library</span> stores all images generated during your session. Drag any result directly onto the canvas or into other tools to build upon your designs.</>
                  ) : (
                    <>The <span className="text-white font-medium italic">Result Window</span> displays your generated images. Switch between batch results using the thumbnails, download individual images, or save them to a project for future reference.</>
                  )}
                </p>
                <button
                  onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                  className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s' }}
                >
                  {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FreestyleField;
